import { type ContactFax } from "@/lib/types/Contact";

import { type createContactFormSchema } from "@/components/features/contact/AddOrEditContactModal/AddContactSlideover";
import {
  ActionConfirmationModal,
  Badge,
  Button,
  Label,
  PhoneNumberFormField,
  SimpleSelectFormField,
  Typography,
} from "@/components/ui";
import { useDeleteContactFax } from "@/lib/data/react-query";
import { CONTACT_FAX_LABELS } from "@/lib/data/static/contactStatics";
import { ContactFaxIcon } from "@/lib/utils/iconUtils";
import pluralize from "pluralize";
import { useState } from "react";
import {
  useFieldArray,
  useWatch,
  type FieldPath,
  type UseFormReturn,
} from "react-hook-form";
import { type z } from "zod";
import { AddOrEditContactFaxSlideover } from "./AddOrEditContactFaxSlideover";
import { formatLocalOrIntlPhoneNumber } from "@/lib/utils/format";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { CheckCircle2, Pencil, Plus, Trash2 } from "lucide-react";
import { ContactFieldRowError } from "@/components/features/contact/ContactFieldRowError/ContactFieldRowError";
import { ContactPrimaryStarButton } from "@/components/features/contact/ContactPrimaryStarButton/ContactPrimaryStarButton";
const newClientRowId = (): string => {
  if (typeof globalThis.crypto.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2);
};

function ContactFaxInlineFields({
  parentForm,
  hasCreatePermission,
  hasEditPermission,
  hasDeletePermission,
  hideLabel,
}: {
  parentForm: UseFormReturn<z.infer<typeof createContactFormSchema>>;
  hasCreatePermission?: boolean;
  hasEditPermission?: boolean;
  hasDeletePermission?: boolean;
  hideLabel?: boolean;
}) {
  const { control, register, getValues, setValue } = parentForm;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "faxes",
  });
  const watchedFaxes = useWatch({ control, name: "faxes" });

  const setPrimary = (index: number) => {
    const n = (getValues("faxes") ?? []).length;
    for (let i = 0; i < n; i++) {
      setValue(`faxes.${i}.isPrimary`, i === index, {
        shouldDirty: true,
      });
    }
  };

  const handleRemove = (index: number) => {
    if (!hasDeletePermission) {
      return;
    }
    const wasPrimary = getValues(`faxes.${index}.isPrimary`);
    remove(index);
    if (wasPrimary) {
      const next = getValues("faxes") ?? [];
      if (next.length > 0) {
        for (let i = 0; i < next.length; i++) {
          setValue(`faxes.${i}.isPrimary`, i === 0, {
            shouldDirty: true,
          });
        }
      }
    }
  };

  const addRow = () => {
    if (!hasCreatePermission) {
      return;
    }
    const list = getValues("faxes") ?? [];
    append({
      id: newClientRowId(),
      number: "",
      label: CONTACT_FAX_LABELS[0],
      description: "",
      isPrimary: list.length === 0,
      extension: "",
      dateModified: "",
    });
  };

  const faxCount = Math.max(fields.length, 1);
  const disabled = !hasEditPermission;

  return (
    <div>
      {!hideLabel && (
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <div className="flex items-center space-x-2">
            <ContactFaxIcon className="h-5 w-5 text-gray-400" />
            <Label className="text-lg font-semibold">
              {pluralize("Fax", faxCount)}
            </Label>
          </div>
        </div>
      )}
      <div className="col-span-full overflow-x-visible">
        {fields.length > 0 && (
          <ul className="space-y-2">
            {fields.map((field, index) => {
              const row = watchedFaxes?.[index];
              const isPrimary = !!row?.isPrimary;
              return (
                <li
                  key={field.id}
                  className="flex flex-col gap-1"
                  data-testid={
                    row?.id ? `contact-fax-item-${row.id}` : undefined
                  }
                >
                  <div className="flex min-w-0 items-stretch gap-2 sm:gap-3">
                    <div className="flex min-h-0 min-w-0 flex-1 flex-col justify-center">
                      <PhoneNumberFormField
                        className="w-full !space-y-0 [&>div:first-child]:hidden"
                        register={register(`faxes.${index}.number`)}
                        disabled={disabled}
                      />
                    </div>
                    <div className="flex w-32 min-w-0 flex-col justify-center self-stretch sm:w-36">
                      <SimpleSelectFormField
                        className="h-10 w-full min-w-0"
                        formItemClassName="!m-0 w-full min-w-0 !space-y-0"
                        placeholder="Label"
                        register={register(`faxes.${index}.label`)}
                        items={CONTACT_FAX_LABELS as readonly string[]}
                        disabled={disabled}
                      />
                    </div>
                    <div className="flex shrink-0 flex-col justify-center self-stretch">
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <ContactPrimaryStarButton
                          isPrimary={isPrimary}
                          disabled={disabled}
                          onClick={() => {
                            setPrimary(index);
                          }}
                          className="h-10 w-10"
                          ariaLabelCurrent="Primary fax"
                          ariaLabelSet="Set as primary fax"
                        />
                        {hasDeletePermission && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-10 w-10 shrink-0"
                            disabled={disabled}
                            onClick={() => {
                              handleRemove(index);
                            }}
                            aria-label="Remove fax number"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                  <ContactFieldRowError
                    control={parentForm.control}
                    fieldPath={
                      `faxes.${index}.number` as FieldPath<
                        z.infer<typeof createContactFormSchema>
                      >
                    }
                  />
                </li>
              );
            })}
          </ul>
        )}
        {hasCreatePermission && (
          <div className="mt-2 w-full self-start">
            <Button
              type="button"
              variant="link"
              className="contact-subitem-add text-muted-foreground h-auto min-h-0 justify-start p-0"
              onClick={addRow}
            >
              <Plus className="h-4 w-4 shrink-0" />
              Add fax
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ContactFaxesSection({
  faxes,
  contactId,
  hasCreatePermission,
  hasEditPermission,
  hasDeletePermission,
  hideLabel,
  parentForm,
}: {
  parentForm?: UseFormReturn<z.infer<typeof createContactFormSchema>>;
  faxes?: ContactFax[];
  contactId: string;
  hasCreatePermission?: boolean;
  hasEditPermission?: boolean;
  hasDeletePermission?: boolean;
  hideLabel?: boolean;
}) {
  const deleteContactFax = useDeleteContactFax();
  const [activeFaxId, setActiveFaxId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  if (parentForm) {
    return (
      <ContactFaxInlineFields
        parentForm={parentForm}
        hasCreatePermission={hasCreatePermission}
        hasEditPermission={hasEditPermission}
        hasDeletePermission={hasDeletePermission}
        hideLabel={hideLabel}
      />
    );
  }

  const faxList = faxes ?? [];
  const parseSortableDate = (value: unknown) =>
    typeof value === "string" ? Date.parse(value) : Number.NaN;
  const sortedFaxList = faxList
    .map((fax, index) => ({ fax, index }))
    .sort((a, b) => {
      const aPrimary = "isPrimary" in a.fax ? !!a.fax.isPrimary : false;
      const bPrimary = "isPrimary" in b.fax ? !!b.fax.isPrimary : false;
      if (aPrimary !== bPrimary) {
        return aPrimary ? -1 : 1;
      }
      const aDate = parseSortableDate(a.fax.dateModified);
      const bDate = parseSortableDate(b.fax.dateModified);
      if (!Number.isNaN(aDate) && !Number.isNaN(bDate) && aDate !== bDate) {
        return aDate - bDate;
      }
      return a.index - b.index;
    })
    .map(({ fax }) => fax);
  const faxCount = Math.max(sortedFaxList.length, 1);
  const showEmptyState = faxList.length === 0 && !isAdding;

  const activeFax = (faxes ?? []).find((fax) => fax.id === activeFaxId);

  const handleDelete = (fax: Pick<ContactFax, "id">) => {
    deleteContactFax.mutate({
      id: fax.id,
      contactId: contactId,
    });
    setActiveFaxId(null);
  };

  return (
    <div>
      {!hideLabel && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center space-x-2">
            <ContactFaxIcon className="h-5 w-5 text-gray-400" />
            <Label className="text-lg font-semibold">
              {pluralize("Fax number", faxCount)}
            </Label>
          </div>
          {hasCreatePermission && (
            <Button
              type="button"
              onClick={() => {
                setIsAdding(true);
              }}
            >
              Create fax number
            </Button>
          )}
        </div>
      )}
      <div className="col-span-full">
        {hideLabel && hasCreatePermission && (
          <div className="flex justify-end">
            <Button
              type="button"
              onClick={() => {
                setIsAdding(true);
              }}
            >
              Create fax number
            </Button>
          </div>
        )}
        {isAdding && (
          <div className="border-border bg-card mt-3 w-full rounded-md border p-3">
            <AddOrEditContactFaxSlideover
              editing={false}
              parentForm={parentForm}
              setOpen={setIsAdding}
              open={isAdding}
              contactId={contactId}
              hasEditPermission={hasEditPermission}
              variant="inline"
            />
          </div>
        )}
        {showEmptyState ? (
          <Typography
            variant="subHeading"
            className="text-muted-foreground mt-3"
          >
            No fax numbers found.
          </Typography>
        ) : (
          <ul className="mt-3 space-y-3">
            {sortedFaxList.map((fax, index) => {
              const isPrimary = "isPrimary" in fax ? fax.isPrimary : undefined;
              const label = fax.label?.trim();
              const isActiveEdit = isEditing && fax.id === activeFaxId;
              if (isActiveEdit) {
                return (
                  <li
                    key={fax.id || `${fax.number}-${index}`}
                    className="border-border bg-card w-full rounded-md border p-3"
                  >
                    <AddOrEditContactFaxSlideover
                      editing={true}
                      parentForm={parentForm}
                      activeContactFax={fax}
                      setOpen={(nextOpen) => {
                        setIsEditing(nextOpen);
                        if (!nextOpen) {
                          setActiveFaxId(null);
                        }
                      }}
                      open={isEditing}
                      contactId={contactId}
                      hasEditPermission={hasEditPermission}
                      hasDeletePermission={hasDeletePermission}
                      onDelete={() => {
                        setIsDeleting(true);
                      }}
                      variant="inline"
                    />
                  </li>
                );
              }
              return (
                <li
                  key={fax.id || `${fax.number}-${index}`}
                  className="border-border bg-card w-full rounded-md border p-3"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <Typography weight="medium" className="truncate">
                        {formatLocalOrIntlPhoneNumber(fax) || "-"}
                      </Typography>
                      {label && (
                        <Badge variant="secondary" className="h-6 shrink-0">
                          {label}
                        </Badge>
                      )}
                      {Boolean(isPrimary) && (
                        <TooltipWrapper message="Primary">
                          <span className="flex items-center">
                            <CheckCircle2
                              className="h-4 w-4 text-emerald-600"
                              aria-hidden="true"
                            />
                            <span className="sr-only">Primary</span>
                          </span>
                        </TooltipWrapper>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        if (!fax.id) {
                          return;
                        }
                        setIsEditing(true);
                        setActiveFaxId(fax.id);
                      }}
                      disabled={!fax.id}
                      aria-label="Edit fax number"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <ActionConfirmationModal
        title={`Are you sure you want to delete this fax?`}
        open={isDeleting && !!activeFax}
        setOpen={setIsDeleting}
        afterActionFn={() => {
          if (activeFax) {
            handleDelete(activeFax);
          }
        }}
      />
    </div>
  );
}
